// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  ColorType,
  createChart,
  LineStyle,
  LineType,
  type AreaData,
  type IChartApi,
} from 'lightweight-charts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDate, formatToMoney, hexToRgba } from '@shared/ui-kit/utils';
import { isUndefined } from '@shared/utils';
import { type DualSeriesAreaChartTooltip, type DualSeriesChartData } from './types';
import { useTranslation } from 'react-i18next';

const buildAreaData = (
  dates: readonly string[],
  values: readonly number[],
): AreaData[] =>
  dates.map((date, index) => ({
    time: date.split('T')[0]!,
    value: values[index] ?? 0,
  }));

const buildCombinedValues = (data: DualSeriesChartData) =>
  data.dates.map((_, index) =>
    data.series.reduce((sum, serie) => sum + (serie.values[index] ?? 0), 0),
  );

export const useDualSeriesAreaChart = (
  data: DualSeriesChartData,
  currency: string,
) => {
  const { i18n } = useTranslation();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi>();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [showSplit, setShowSplit] = useState(true);
  const [tooltipValues, setTooltipValues] = useState<DualSeriesAreaChartTooltip>();

  const combinedValues = useMemo(() => buildCombinedValues(data), [data]);

  const visibleSeries = useMemo(
    () =>
      showSplit
        ? data.series.map(serie => ({
            id: serie.id,
            label: serie.label,
            color: serie.color,
            data: buildAreaData(data.dates, serie.values),
          }))
        : [
            {
              id: 'combined',
              label: data.combinedSeries.label,
              color: data.combinedSeries.color,
              data: buildAreaData(data.dates, combinedValues),
            },
          ],
    [combinedValues, data, showSplit],
  );

  const hasData = visibleSeries.some(serie => serie.data.length > 0);

  const onToggleSplit = useCallback(() => setShowSplit(current => !current), []);

  useEffect(() => {
    if (!chartContainerRef.current || !hasData) return;

    chartRef.current = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#edf0f9' },
        textColor: '#515F70',
        fontFamily: 'Poppins',
        fontSize: 12,
      },
      width: chartContainerRef.current.clientWidth,
      height: Math.max(chartContainerRef.current.clientHeight, 228),
      handleScroll: false,
      handleScale: false,
      grid: {
        vertLines: {
          visible: false,
        },
        horzLines: {
          style: LineStyle.LargeDashed,
        },
      },
      crosshair: {
        horzLine: { visible: false, labelVisible: false },
        vertLine: { labelVisible: false },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: {
          bottom: 0,
        },
      },
      timeScale: {
        allowBoldLabels: false,
        borderVisible: false,
      },
    });

    const renderedSeries = visibleSeries.map(serie => {
      const areaSeries = chartRef.current!.addAreaSeries({
        lineColor: serie.color,
        topColor: serie.color,
        bottomColor: hexToRgba(serie.color, 0.3),
        lineType: LineType.Simple,
        lineWidth: 2,
        crosshairMarkerVisible: false,
        priceFormat: { type: 'volume' },
      });

      areaSeries.setData(serie.data);

      return {
        areaSeries,
        label: serie.label,
        color: serie.color,
      };
    });

    chartRef.current.timeScale().fitContent();

    /* istanbul ignore next */
    chartRef.current.subscribeCrosshairMove(param => {
      if (isUndefined(param.point) || !param.time) {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }

        return;
      }

      const items = renderedSeries.flatMap(({ areaSeries, label, color }) => {
        const serie = param.seriesData.get(areaSeries) as AreaData | undefined;

        return serie
          ? [
              {
                label,
                value: formatToMoney(serie.value, currency),
                color,
              },
            ]
          : [];
      });

      setTooltipValues({
        date: formatDate(String(param.time), 'medium', i18n.language) ?? '',
        items,
      });

      if (!tooltipRef.current || !chartRef.current) return;

      const container = chartContainerRef.current!;
      const toolTipWidth = tooltipRef.current.clientWidth;
      const toolTipHeight = tooltipRef.current.clientHeight;
      const toolTipMargin = 15;
      const y = param.point.y;
      const left =
        param.point.x + toolTipMargin > container.clientWidth - toolTipWidth
          ? param.point.x - toolTipMargin - toolTipWidth
          : param.point.x + toolTipMargin;
      const top =
        y + toolTipMargin > container.clientHeight - toolTipHeight
          ? y - toolTipHeight - toolTipMargin
          : y + toolTipMargin;

      tooltipRef.current.style.left = `${left}px`;
      tooltipRef.current.style.top = `${top}px`;
      tooltipRef.current.style.display = 'block';
    });

    resizeObserverRef.current = new ResizeObserver(entries => {
      const { width, height } = entries[0]!.contentRect;

      chartRef.current?.applyOptions({
        width,
        height: Math.max(height, 228),
      });
      chartRef.current?.timeScale().fitContent();
    });

    resizeObserverRef.current.observe(chartContainerRef.current);

    return () => {
      resizeObserverRef.current?.disconnect();
      chartRef.current?.remove();
    };
  }, [currency, hasData, i18n.language, visibleSeries]);

  return {
    chartContainerRef,
    tooltipRef,
    tooltipValues,
    showSplit,
    onToggleSplit,
    hasData,
  };
};
